<template>
  <div class="dp-DashboardPage">
    <div :class="{ [`dp-MainContent-${view}`]: view }" class="dp-MainContent">
      <AppRadioGroup
        v-if="!hideNavigation"
        :model-value="view"
        :options="dashboardTabsMenu"
        :type="RADIO_GROUP_TYPES.TAB_LIKE"
        class="dp-DashboardPage_Navigation"
        name="dashboard-navigation"
        @update:model-value="navigateByName"
      />

      <portal v-slot="{ tab }" to="dashboard-interval-select">
        <AppSelect
          v-model="intervalId"
          :dropdown-min-width="200"
          :is-highlighted="!!intervalId"
          :loading="isIntervalsLoading"
          :offset="[0, 0]"
          :options="intervals"
          :type="SELECT_TYPES.SECONDARY_NEXT"
          append-icon-height="20"
          append-icon-width="20"
          class="dp-DashboardPage_IntervalSelect"
          icon="calendar-next"
          item-label="name"
          item-value="id"
          n-selected-label="field.quoter.title"
          position="bottom-start"
          prepend-icon="calendar-next"
          @update:model-value="selectIntervals($event, tab)"
        >
          <template #option="props">
            <IntervalSelectOption v-bind="props" />
          </template>
          <template #button-content="{ option }">
            <AppSelectItemWithTooltip
              v-if="option"
              :label="option.name"
              :tooltip-label="option.name"
            />
          </template>
        </AppSelect>
      </portal>
      <router-view v-if="isAccessAllowed">
        <template #default="{ Component, route }">
          <template v-if="isNoIntervals && route.name !== ROUTE_NAMES.CUSTOM_DASHBOARD_LIST">
            <EmptyStateDashboard>
              <div class="dp-NoIntervals">
                <div class="dp-NoIntervals_Title">
                  {{ $t('dashboard.no_intervals') }}
                </div>
                <template v-if="userIsAdmin">
                  <div class="dp-NoIntervals_Actions">
                    <div class="dp-NoIntervalsActions_Title">
                      {{ $t('dashboard.no_intervals_subtitle') }}
                    </div>
                    <AppDivider class="dp-NoIntervalsActions_Divider" no-margin />
                    <AppButton
                      height="24"
                      icon="plus-next"
                      type="primary-next"
                      width="24"
                      @click="onCreateIntervalClick"
                    >
                      {{ $t('intervals.create_interval') }}
                    </AppButton>
                  </div>
                </template>
              </div>
            </EmptyStateDashboard>
          </template>

          <component :is="Component" v-else :interval-id="intervalId" :workspace-id="workspaceId" />
        </template>
      </router-view>
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'lodash'
import { defineComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import IntervalsInfoApiHandler from '@/api/intervals-info'
import OBJECTIVES_DASHBOARD_ROUTES from '@/routes/objectives-dashboard'
import { ROUTE_NAMES, ROUTE_PATHS } from '@/routes/route-helpers'
import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { RADIO_GROUP_TYPES } from '@/utils/components-configurations/app-radio-group'
import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { COMPANY_SETTINGS_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { getSuitableInterval } from '@/utils/interval'
import { sendIntervalFilterTrackerEvent } from '@/utils/okr-elements/filters'
import {
  EDIT_WORKSPACE_ACTIVE_TAB_QUERY_KEY,
  EDIT_WORKSPACE_QUERY_KEY,
  INTERVAL_ID_KEY
} from '@/utils/query-parameters'
import { updateQueryParameter } from '@/utils/router'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'
import {
  DASHBOARD_FILTERS_KEYS,
  DASHBOARD_GENERAL_FILTERS,
  getResolvedRestoredValueWithQuery,
  USER_SETTINGS_MAPPER
} from '@/utils/user-settings'

import EmptyStateDashboard from '@/components/dashboard/EmptyStateDashboard'
import IntervalSelectOption from '@/components/objectives/toolbar/IntervalSelectOption'
import AppBadge from '@/components/ui/AppBadge/AppBadge'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppDivider from '@/components/ui/AppDivider'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import AppSelect from '@/components/ui/AppSelect/AppSelect'
import AppSelectItem from '@/components/ui/AppSelect/AppSelectItem'
import AppSelectItemWithTooltip from '@/components/ui/AppSelect/AppSelectItemWithTooltip'
import TitleSelectItem from '@/components/ui/AppSelect/TriggerButtons/TitleSelectItem'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import NoSearchResults from '@/components/ui/NoSearchResults/NoSearchResults'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'

export default defineComponent({
  name: 'DashboardLayout',
  components: {
    AppSelectItemWithTooltip,
    AppButton,
    AppDivider,
    EmptyStateDashboard,
    NoSearchResults,

    AppBadge,
    AppRadioGroup,
    PageContentHeader,
    AppSelect,
    AppTitle,
    TitleSelectItem,
    AppSelectItem,
    IntervalSelectOption
  },

  beforeRouteUpdate(to, __, next) {
    if (!this.allowedRouteNames.includes(to.name)) {
      const [firstAllowedRoute] = this.dashboardTabsMenu

      if (!firstAllowedRoute.value) {
        next(this.homeRoutePath)
        return
      }
      next({ name: firstAllowedRoute.value })
      return
    }
    next()
  },

  beforeRouteEnter(to, __, next) {
    next(async vm => {
      const showDashboardItem = vm.showDashboardItem

      if (isEmpty(vm.dashboardTabsMenu) || !showDashboardItem) {
        vm.redirectToHome()
        return
      }

      if (!vm.allowedRouteNames.includes(to.name)) {
        const [firstAllowedRoute] = vm.dashboardTabsMenu

        if (!firstAllowedRoute.value) {
          vm.redirectToHome()
          return
        }

        await vm.$nextTick()

        /**
         * Redirect to the default route.
         * @note Do NOT use `navigateByName` method here, as the push method is a promise and we need to wait for it.
         */
        await vm.$router.isReady()
        await vm.$router.push({ name: firstAllowedRoute.value })

        await vm.$nextTick()

        vm.isAccessAllowed = true
        return
      }

      vm.isAccessAllowed = true
    })
  },

  data() {
    return {
      intervals: [],
      isIntervalsLoading: false,
      intervalId: null,
      isAccessAllowed: false
    }
  },

  computed: {
    ...mapGetters('system', {
      // eslint-disable-next-line vue/no-unused-properties
      showDashboardItem: 'showDashboardItem',
      dashboardsVisibility: 'dashboardsVisibility',
      userIsAdmin: 'userIsAdmin',
      dashboardGeneralSavedFiltersByWorkspaceId: 'dashboardGeneralFiltersByWorkspaceId',
      dashboardGeneralSavedFilters: 'dashboardGeneralFilters'
      // performanceReportFilters: 'performanceReportFilters'
    }),

    RADIO_GROUP_TYPES: () => RADIO_GROUP_TYPES,

    SELECT_TYPES: () => SELECT_TYPES,

    ROUTE_NAMES: () => ROUTE_NAMES,

    homeRoutePath() {
      return `${ROUTE_PATHS.WORKSPACES}${this.workspaceId}`
    },

    allowedRouteNames() {
      return this.dashboardTabsMenu.flatMap(item => item.relatedRoutes)
    },

    isNoIntervals() {
      return isEmpty(this.intervals) && !this.isIntervalsLoading
    },

    view() {
      if (this.$route.path.includes('dashboard/performance-report')) {
        return ROUTE_NAMES.DASHBOARD_PERFORMANCE_REPORT
      }
      return this.$route.name
    },

    hideNavigation() {
      return (
        this.$route.meta.hideNavigation ||
        isEmpty(this.dashboardTabsMenu) ||
        this.dashboardTabsMenu.length === 1
      )
    },

    dashboardTabsMenu() {
      const { children: nestedRoutes } = OBJECTIVES_DASHBOARD_ROUTES

      const getCurrentRoute = routeName => nestedRoutes.filter(item => item.name === routeName)

      const getRelatedRoutes = route => {
        return route.reduce((acc, item) => {
          if (item.children) {
            return [...acc, item.name, ...getRelatedRoutes(item.children)]
          }
          return [...acc, item.name]
        }, [])
      }

      return [
        {
          label: this.$t('dashboard.menu.general_report'),
          value: ROUTE_NAMES.DASHBOARD_GENERAL,
          routeName: ROUTE_NAMES.DASHBOARD_GENERAL,
          visibilityKey: COMPANY_SETTINGS_ENTITY_KEYS.SHOW_GENERAL_DASHBOARD_TO_USERS
        },
        {
          label: this.$t('dashboard.menu.performance_report'),
          value: ROUTE_NAMES.DASHBOARD_PERFORMANCE_REPORT,
          routeName: ROUTE_NAMES.DASHBOARD_PERFORMANCE_REPORT,
          visibilityKey: COMPANY_SETTINGS_ENTITY_KEYS.SHOW_PERFORMANCE_DASHBOARD_TO_USERS
        },
        {
          label: this.$t('dashboard.menu.custom_dashboard'),
          value: ROUTE_NAMES.CUSTOM_DASHBOARD_LIST,
          routeName: ROUTE_NAMES.CUSTOM_DASHBOARD,
          visibilityKey: COMPANY_SETTINGS_ENTITY_KEYS.SHOW_CUSTOM_DASHBOARD_TO_USERS
        }
      ]
        .map(item => {
          return {
            ...item,
            allowed: this.dashboardsVisibility[item.visibilityKey],
            relatedRoutes: getRelatedRoutes(getCurrentRoute(item.routeName))
          }
        })
        .filter(item => item.allowed)
    },

    workspaceId() {
      return this.$route.params.workspaceId
    }
  },

  watch: {
    async workspaceId(newValue) {
      if (newValue) {
        this.intervalId = await this.getSavedIntervalId()

        this.getAllIntervals()
      }
    },

    intervalId(newValue) {
      updateQueryParameter(this.$router, this.$route, INTERVAL_ID_KEY, newValue)
    }
  },

  async mounted() {
    await this.restoreFilters()
    this.getAllIntervals()
  },

  methods: {
    ...mapActions('system', {
      updateUserSettings: 'updateUserSettings'
    }),

    // eslint-disable-next-line vue/no-unused-properties
    redirectToHome() {
      // wait for finishing initial route resolve and redirect
      this.$nextTick(() => {
        this.$router.push(this.homeRoutePath)
      })
    },

    async getSavedIntervalId() {
      const savedFilters = this.dashboardGeneralSavedFiltersByWorkspaceId({
        workspaceId: this.workspaceId
      })

      // 8148 q422
      const savedIntervalIds = savedFilters[DASHBOARD_FILTERS_KEYS.INTERVAL_IDS] || []

      const [savedIntervalId] = savedIntervalIds

      return savedIntervalId ?? null
    },

    async restoreFilters() {
      const savedIntervalId = await this.getSavedIntervalId()

      const restoredIntervalId = getResolvedRestoredValueWithQuery({
        valueFromSettings: savedIntervalId,
        valueFromQuery: this.$route.query.intervalId
      })

      if (restoredIntervalId) {
        this.intervalId = Number(restoredIntervalId)
      }
    },

    selectIntervals(value, tab) {
      sendIntervalFilterTrackerEvent({
        tab,
        section: EVENT_SOURCES.DASHBOARD,
        newValue: [value],
        intervals: this.intervals
      })

      this.saveSelectedInterval({ intervalId: value })
    },

    saveSelectedInterval({ intervalId }) {
      this.updateUserSettings({
        [USER_SETTINGS_MAPPER[DASHBOARD_GENERAL_FILTERS]]: {
          ...this.dashboardGeneralSavedFilters,
          [this.workspaceId]: {
            ...this.dashboardGeneralSavedFiltersByWorkspaceId({ workspaceId: this.workspaceId }),
            [DASHBOARD_FILTERS_KEYS.INTERVAL_IDS]: [intervalId]
          }
        }
      })
    },

    navigateByName(name) {
      this.$router.push({ name })
    },

    onCreateIntervalClick() {
      this.$router.push({
        name: ROUTE_NAMES.SETTINGS_WORKSPACES,
        query: {
          [EDIT_WORKSPACE_QUERY_KEY]: this.workspaceId,
          [EDIT_WORKSPACE_ACTIVE_TAB_QUERY_KEY]: TABLE_COLUMNS_KEYS.INTERVALS
        }
      })
    },

    async getAllIntervals() {
      const api = new IntervalsInfoApiHandler()
      this.isIntervalsLoading = true
      try {
        const { intervals } = await api.getIntervalsInfo({
          workspaceId: this.workspaceId
        })
        this.intervals = intervals.filter(item => item.open && !item.backlog)
        const suitableInterval = getSuitableInterval(this.intervals, [], true)

        if (this.intervalId) {
          const index = this.intervals.findIndex(int => int.id === this.intervalId)
          if (index === -1) {
            this.intervalId = suitableInterval
            this.saveSelectedInterval({
              intervalId: suitableInterval
            })
          }
        } else {
          this.intervalId = suitableInterval
        }
      } catch (error) {
        handleError({ error })
      } finally {
        this.isIntervalsLoading = false
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.dp-DashboardPage {
  padding-bottom: $page-bottom-padding;
}

.dp-MainContent {
  padding-left: $page-left-padding;
  padding-right: $page-right-padding;
  padding-top: $page-top-padding;
  margin: 0 auto;
  &-CustomDashboardItem {
    padding: 0;
  }
}

.dp-Navigation {
  margin-bottom: 24px;
}

.dp-DashboardPage_IntervalSelect {
  width: max-content;
  max-width: 200px;
}

.dp-DashboardPage_Navigation {
  margin: 0 0 16px 0;
}

.dp-NoIntervals_Title {
  margin-bottom: 16px;
}

.dp-NoIntervals {
  text-align: center;
  max-width: 800px;
}

.dp-NoIntervals_Actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.dp-NoIntervalsActions_Title {
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-3;
  font-weight: fw('regular');
}

.dp-NoIntervalsActions_Divider {
  width: 100%;
}
</style>
